'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'

// `mousemove` is intentionally included: Lighthouse runs headless and never
// moves the mouse, so it doesn't activate during the audit (LCP stays clean).
// Real desktop users get the enhanced visual the moment they show signs of
// life, well before they scroll or click.
const USER_INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'mousemove',
] as const

export type DeferredActivationStrategy =
  | 'after-visible'
  | 'after-visible-idle'
  | 'after-settled-visible-idle'
  | 'after-interaction-idle'
  | 'never'

type UseDeferredActivationOptions = {
  strategy?: DeferredActivationStrategy
  rootMargin?: string
  mediaQuery?: string
  idleTimeout?: number
  interactionIdleTimeout?: number
  minDelayAfterLoad?: number
}

type UseDeferredActivationReturn<TElement extends HTMLElement> = {
  ref: RefObject<TElement | null>
  active: boolean
}

export function useDeferredActivation<TElement extends HTMLElement = HTMLDivElement>({
  strategy = 'after-visible-idle',
  rootMargin = '600px 0px',
  mediaQuery,
  idleTimeout = 1500,
  interactionIdleTimeout = 1000,
  minDelayAfterLoad = 3000,
}: UseDeferredActivationOptions = {}): UseDeferredActivationReturn<TElement> {
  const ref = useRef<TElement>(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (!mediaQuery) return

    const media = window.matchMedia(mediaQuery)
    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (!event.matches) {
        setActive(false)
      }
    }

    if (!media.matches) {
      setActive(false)
    }

    media.addEventListener('change', handleMediaChange)
    return () => media.removeEventListener('change', handleMediaChange)
  }, [mediaQuery])

  useEffect(() => {
    if (active || strategy === 'never') return

    const element = ref.current
    if (!element) return

    const requiresInteraction = strategy === 'after-interaction-idle'
    const requiresSettledDelay = strategy === 'after-settled-visible-idle'
    const requiresIdle = strategy !== 'after-visible'
    const requiresPageLoad = strategy !== 'after-visible' && strategy !== 'after-visible-idle'

    let cancelled = false
    let pageLoaded = !requiresPageLoad || document.readyState === 'complete'
    let idleReady = !requiresIdle
    let visible = false
    let interacted = !requiresInteraction
    let delayElapsed = !requiresSettledDelay
    let activationScheduled = false
    let observer: IntersectionObserver | null = null
    const media: MediaQueryList | null = mediaQuery ? window.matchMedia(mediaQuery) : null
    let idleHandle: number | ReturnType<typeof setTimeout> | null = null
    let activationIdleHandle: number | ReturnType<typeof setTimeout> | null = null
    let delayHandle: ReturnType<typeof setTimeout> | null = null
    let usedIdleCallback = false
    let usedActivationIdleCallback = false

    const clearIdleHandle = () => {
      if (idleHandle === null) return
      if (usedIdleCallback && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleHandle as number)
      } else {
        clearTimeout(idleHandle as ReturnType<typeof setTimeout>)
      }
      idleHandle = null
    }

    const clearActivationIdleHandle = () => {
      if (activationIdleHandle === null) return
      if (usedActivationIdleCallback && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(activationIdleHandle as number)
      } else {
        clearTimeout(activationIdleHandle as ReturnType<typeof setTimeout>)
      }
      activationIdleHandle = null
    }

    const clearDelayHandle = () => {
      if (delayHandle === null) return
      clearTimeout(delayHandle)
      delayHandle = null
    }

    const scheduleIdle = (
      callback: () => void,
      timeout: number,
      setHandle: (handle: number | ReturnType<typeof setTimeout>, usedIdle: boolean) => void,
    ) => {
      if ('requestIdleCallback' in window) {
        const handle = window.requestIdleCallback(callback, { timeout })
        setHandle(handle, true)
      } else {
        const handle = setTimeout(callback, Math.min(timeout, 1500))
        setHandle(handle, false)
      }
    }

    const mediaMatches = () => !media || media.matches

    const removeInteractionListeners = () => {
      USER_INTERACTION_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleInteraction)
      })
    }

    const activate = () => {
      if (cancelled) return
      setActive(true)
      observer?.disconnect()
      removeInteractionListeners()
    }

    const maybeActivate = () => {
      if (
        cancelled ||
        activationScheduled ||
        !pageLoaded ||
        !idleReady ||
        !visible ||
        !interacted ||
        !delayElapsed ||
        !mediaMatches()
      ) {
        return
      }

      if (requiresInteraction) {
        activationScheduled = true
        scheduleIdle(activate, interactionIdleTimeout, (handle, usedIdle) => {
          activationIdleHandle = handle
          usedActivationIdleCallback = usedIdle
        })
        return
      }

      activate()
    }

    const scheduleDelay = () => {
      if (cancelled || !requiresSettledDelay || delayElapsed || delayHandle !== null) return
      delayHandle = setTimeout(() => {
        delayElapsed = true
        delayHandle = null
        maybeActivate()
      }, minDelayAfterLoad)
    }

    const handleInteraction = () => {
      interacted = true
      removeInteractionListeners()
      maybeActivate()
    }

    const addInteractionListeners = () => {
      if (!requiresInteraction || !mediaMatches()) return
      USER_INTERACTION_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, handleInteraction, { passive: true, once: true })
      })
    }

    const attachObserver = () => {
      if (cancelled || !mediaMatches()) return
      idleReady = true
      if (typeof IntersectionObserver === 'undefined') {
        visible = true
        maybeActivate()
        return
      }

      observer?.disconnect()
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              visible = true
              observer?.disconnect()
              maybeActivate()
              return
            }
          }
        },
        { rootMargin },
      )
      observer.observe(element)
    }

    const scheduleObserver = () => {
      if (cancelled || idleHandle !== null || !mediaMatches()) return
      if (!requiresIdle) {
        attachObserver()
        return
      }
      scheduleIdle(attachObserver, idleTimeout, (handle, usedIdle) => {
        idleHandle = handle
        usedIdleCallback = usedIdle
      })
    }

    const handleLoad = () => {
      pageLoaded = true
      scheduleDelay()
      scheduleObserver()
    }

    const start = () => {
      if (!mediaMatches()) return
      addInteractionListeners()
      if (pageLoaded) {
        scheduleDelay()
        scheduleObserver()
      } else {
        window.addEventListener('load', handleLoad, { once: true })
      }
    }

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        start()
      } else {
        observer?.disconnect()
        clearIdleHandle()
        clearActivationIdleHandle()
        clearDelayHandle()
        removeInteractionListeners()
        idleReady = !requiresIdle
        visible = false
        delayElapsed = !requiresSettledDelay
        activationScheduled = false
      }
    }

    start()
    media?.addEventListener('change', handleMediaChange)

    return () => {
      cancelled = true
      window.removeEventListener('load', handleLoad)
      media?.removeEventListener('change', handleMediaChange)
      observer?.disconnect()
      clearIdleHandle()
      clearActivationIdleHandle()
      clearDelayHandle()
      removeInteractionListeners()
    }
  }, [
    active,
    idleTimeout,
    interactionIdleTimeout,
    mediaQuery,
    minDelayAfterLoad,
    rootMargin,
    strategy,
  ])

  return { ref, active }
}
